/**
 * VELUNA HEALTH — ein kleines Modul für alle vh-* Sections.
 * - Scroll-Reveal (einmalig, respektiert reduced motion)
 * - Rail-Pfeile (Desktop)
 * - Warenkorb-Buttons über Shopify.actions (Horizon-Warenkorb bleibt die Quelle)
 * - Vorratsauswahl auf der PDP (setzt die Menge im Horizon-Formular)
 * - Quiz
 */

document.documentElement.classList.add('vh-js');

/* ---------- Reveal ---------- */
const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = entry.target;
            el.classList.add('is-visible');
            revealObserver.unobserve(el);
            // Nach dem Einblenden Animations-Styles entfernen: sonst behält Chrome eine eigene
            // Grafikebene und mix-blend-mode (Packshots auf Blush) greift nicht mehr.
            const delay = parseFloat(getComputedStyle(el).transitionDelay) * 1000 || 0;
            setTimeout(() => {
              el.removeAttribute('data-vh-reveal');
              el.classList.remove('is-visible');
            }, 700 + delay);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      )
    : null;

function initReveal(root) {
  root.querySelectorAll('[data-vh-reveal]:not(.is-visible)').forEach((el) => {
    if (revealObserver) revealObserver.observe(el);
    else el.removeAttribute('data-vh-reveal');
  });
}

/* ---------- Rails ---------- */
function initRails(root) {
  root.querySelectorAll('[data-vh-rail-controls]').forEach((controls) => {
    if (controls.dataset.vhRailReady) return;
    controls.dataset.vhRailReady = 'true';
    const rail = document.getElementById(controls.dataset.vhRailControls);
    if (!rail) return;
    controls.addEventListener('click', (event) => {
      const button = event.target.closest('[data-vh-dir]');
      if (!button) return;
      const item = rail.firstElementChild;
      const step = item ? item.getBoundingClientRect().width + 20 : rail.clientWidth * 0.8;
      rail.scrollBy({ left: Number(button.dataset.vhDir) * step, behavior: 'smooth' });
    });
  });
}

/* ---------- Cart ---------- */
const ROOT = () => window.Shopify?.routes?.root || '/';

/** Fallback, der immer funktioniert: klassisches Formular an /cart/add (Shopify leitet auf /cart). */
function submitCartForm(lines) {
  const form = document.createElement('form');
  form.method = 'post';
  form.action = `${ROOT()}cart/add`;
  form.style.display = 'none';
  lines.forEach((line, i) => {
    [['id', line.id], ['quantity', line.quantity]].forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `items[${i}][${key}]`;
      input.value = String(value);
      form.appendChild(input);
    });
  });
  const ret = document.createElement('input');
  ret.type = 'hidden';
  ret.name = 'return_to';
  ret.value = `${ROOT()}cart`;
  form.appendChild(ret);
  document.body.appendChild(form);
  form.submit();
}

/** Warenkorb-Schublade neu laden und öffnen (Horizon-Section-Rendering). */
async function refreshAndOpenDrawer(lines, cart) {
  let handled = false;
  try {
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    const deferred = CartLinesUpdateEvent.createPromise();
    const target = document.querySelector('theme-drawer#cart-drawer') || document;
    target.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: `gid://shopify/ProductVariant/${line.id}`, quantity: Number(line.quantity) })),
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'veluna', itemCount: lines.reduce((n, l) => n + Number(l.quantity), 0) },
    });
    handled = true;
  } catch (error) {
    console.warn('[veluna] Horizon-Event nicht verfügbar', error);
  }

  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (handled && drawer?.open) {
    setTimeout(() => drawer.open(), 150);
    return;
  }
  // Keine Schublade oder Event nicht angekommen: Warenkorbseite zeigen.
  window.location.href = `${ROOT()}cart`;
}

async function addLines(lines) {
  const clean = lines
    .map((line) => ({ id: Number(line.id), quantity: Number(line.quantity) || 1 }))
    .filter((line) => line.id > 0);
  if (!clean.length) throw new Error('keine Variante');

  let response;
  try {
    response = await fetch(`${ROOT()}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: clean }),
    });
  } catch (error) {
    console.warn('[veluna] cart/add.js nicht erreichbar, nutze Formular', error);
    submitCartForm(clean);
    return;
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    console.warn('[veluna] cart/add.js abgelehnt', response.status, payload);
    // 422 = echter Shopify-Grund (z. B. ausverkauft) → Meldung zeigen; alles andere → Formular-Fallback.
    if (response.status === 422) throw new Error(payload.description || payload.message || 'Nicht verfügbar');
    submitCartForm(clean);
    return;
  }

  const cart = await fetch(`${ROOT()}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .catch(() => null);
  if (!cart) {
    window.location.href = `${ROOT()}cart`;
    return;
  }
  await refreshAndOpenDrawer(clean, cart);
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-vh-add]');
  if (!button) return;
  event.preventDefault();

  let lines = [];
  try {
    lines = JSON.parse(button.dataset.vhAdd);
  } catch (error) {
    return;
  }

  const label = button.querySelector('[data-vh-add-label]') || button;
  const original = label.textContent;
  button.setAttribute('aria-busy', 'true');

  try {
    await addLines(lines);
    label.textContent = button.dataset.vhAdded || original;
    setTimeout(() => (label.textContent = original), 2200);
  } catch (error) {
    console.warn('[veluna] add to cart failed', error);
    label.textContent = (error && error.message && error.message.length < 40 ? error.message : button.dataset.vhError) || original;
    setTimeout(() => (label.textContent = original), 2600);
  } finally {
    button.removeAttribute('aria-busy');
  }
});

/* ---------- UGC Video: Tippen zum Abspielen ---------- */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-vh-video-toggle], [data-vh-video]');
  if (!toggle) return;
  const frame = toggle.closest('.vh-ugc__frame');
  const video = frame?.querySelector('video');
  if (!video) return;
  if (video.paused) {
    document.querySelectorAll('.vh-ugc__frame.is-playing video').forEach((other) => {
      if (other !== video) {
        other.pause();
        other.closest('.vh-ugc__frame').classList.remove('is-playing');
      }
    });
    video.muted = false;
    video.play().catch(() => {
      video.muted = true;
      video.play();
    });
    frame.classList.add('is-playing');
  } else {
    video.pause();
    frame.classList.remove('is-playing');
  }
});

/* ---------- Galerie-Wechsel bei Bundle-Auswahl ---------- */
function vhGalleryHost() {
  const main = document.querySelector('.shopify-section, main');
  if (!main) return null;
  let host = document.querySelector('[data-vh-offer-gallery]');
  if (host) return host;
  const gallery =
    document.querySelector('product-media-gallery') ||
    document.querySelector('[class*="media-gallery"]') ||
    document.querySelector('.product-media-container');
  if (!gallery || !gallery.parentElement) return null;
  host = document.createElement('div');
  host.className = 'vh-offer-gallery';
  host.setAttribute('data-vh-offer-gallery', '');
  host.innerHTML =
    '<div class="vh-offer-gallery__track" data-track></div>' +
    '<div class="vh-offer-gallery__dots" data-dots aria-hidden="true"></div>' +
    '<p class="vh-offer-gallery__label" data-label></p>';
  gallery.parentElement.insertBefore(host, gallery);
  host.dataset.vhSibling = 'true';
  return host;
}

function syncGallery(radio) {
  const host = vhGalleryHost();
  if (!host) return;
  const gallery = host.nextElementSibling;
  const urls = (radio.dataset.gallery || '').split('|').filter(Boolean);

  if (!urls.length) {
    host.classList.remove('is-visible');
    if (gallery) gallery.style.display = '';
    return;
  }

  const track = host.querySelector('[data-track]');
  const dots = host.querySelector('[data-dots]');
  const label = host.querySelector('[data-label]');
  const alt = radio.dataset.galleryAlt || '';

  if (host.dataset.current !== urls.join('|')) {
    host.dataset.current = urls.join('|');
    track.innerHTML = urls
      .map((url, i) => `<img src="${url}" alt="${alt}" loading="${i === 0 ? 'eager' : 'lazy'}" width="1200" height="1200">`)
      .join('');
    dots.innerHTML = urls.map((_, i) => `<span class="vh-offer-gallery__dot${i === 0 ? ' is-active' : ''}"></span>`).join('');
    track.scrollTo({ left: 0 });
    track.onscroll = () => {
      const index = Math.round(track.scrollLeft / track.clientWidth);
      [...dots.children].forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };
  }
  label.textContent = alt || '';
  host.classList.add('is-visible');
  if (gallery) gallery.style.display = 'none';
}

/* ---------- Angebots-Kacheln (PDP) ---------- */
function initOffers(root) {
  root.querySelectorAll('[data-vh-offer]').forEach((offer) => {
    if (offer.dataset.vhOfferReady) return;
    offer.dataset.vhOfferReady = 'true';
    const cta = offer.querySelector('[data-vh-offer-cta]');
    const sticky = offer.querySelector('[data-vh-offer-sticky]');
    const stickyLabel = offer.querySelector('[data-vh-offer-sticky-label]');

    const selected = () => offer.querySelector('.vh-offer__tile input:checked:not(:disabled)') || offer.querySelector('.vh-offer__tile input:not(:disabled)');
    const sync = () => {
      const radio = selected();
      offer.querySelectorAll('.vh-offer__tile').forEach((tile) => tile.classList.toggle('is-selected', tile.contains(radio)));
      if (!radio) {
        if (cta) cta.disabled = true;
        return;
      }
      radio.checked = true;
      offer.querySelectorAll('[data-vh-offer-price]').forEach((el) => (el.textContent = radio.dataset.price));
      syncGallery(radio);
      if (stickyLabel) stickyLabel.textContent = radio.dataset.label;
      if (cta) cta.dataset.vhAdd = radio.dataset.lines;
    };
    offer.addEventListener('change', sync);
    sync();

    if (sticky && cta && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        const btn = sticky.querySelector('button');
        if (btn) btn.tabIndex = show ? 0 : -1;
      });
      io.observe(cta);
      sticky.querySelector('[data-vh-offer-sticky-btn]')?.addEventListener('click', () => cta.click());
    }
  });
}

/* ---------- Scroll-Stack (Karten nacheinander) ---------- */
function initStacks(root) {
  root.querySelectorAll('[data-vh-stack]').forEach((stack) => {
    if (stack.dataset.vhStackReady) return;
    stack.dataset.vhStackReady = 'true';

    const cards = [...stack.querySelectorAll('[data-vh-stack-card]')];
    const dots = [...stack.querySelectorAll('[data-vh-stack-dot]')];
    const sentinels = [...stack.querySelectorAll('[data-vh-stack-sentinel]')];
    if (!cards.length || !sentinels.length) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) {
      cards.forEach((c) => c.classList.add('is-active'));
      return;
    }

    let current = -1;
    const setActive = (index) => {
      if (index === current) return;
      current = index;
      cards.forEach((card, i) => {
        card.classList.toggle('is-active', i === index);
        card.classList.toggle('is-past', i < index);
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(sentinels.indexOf(entry.target));
        });
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 }
    );
    sentinels.forEach((sentinel) => io.observe(sentinel));
    setActive(0);
  });
}

/* ---------- Punkte-Hintergrund (leichtgewichtig, ohne three.js) ---------- */
function initDots(root) {
  root.querySelectorAll('[data-vh-dots]').forEach((canvas) => {
    if (canvas.dataset.vhDotsReady) return;
    canvas.dataset.vhDotsReady = 'true';
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const ROSE = 'rgba(209, 156, 164, ';
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let points = [];
    let raf = null;
    let running = false;

    const build = () => {
      const rect = canvas.getBoundingClientRect();
      w = Math.max(1, Math.round(rect.width));
      h = Math.max(1, Math.round(rect.height));
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const density = w < 520 ? 4400 : 6200;
      const count = Math.max(18, Math.min(70, Math.round((w * h) / density)));
      points = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        r: 1 + Math.random() * 1.6,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = w + 10;
        if (p.x > w + 10) p.x = -10;
        if (p.y < -10) p.y = h + 10;
        if (p.y > h + 10) p.y = -10;
        for (let j = i + 1; j < points.length; j++) {
          const q = points[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const dist = Math.hypot(dx, dy);
          if (dist < 92) {
            ctx.strokeStyle = ROSE + (0.16 * (1 - dist / 92)).toFixed(3) + ')';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.stroke();
          }
        }
        ctx.fillStyle = ROSE + '0.5)';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };

    const start = () => {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(draw);
    };
    const stop = () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = null;
    };

    build();
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? start() : stop()),
        { rootMargin: '150px' }
      ).observe(canvas);
    } else {
      start();
    }

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        dpr = Math.min(window.devicePixelRatio || 1, 2);
        build();
      }, 180);
    });
  });
}

/* ---------- Quiz ---------- */
function initQuiz(root) {
  root.querySelectorAll('[data-vh-quiz]').forEach((quiz) => {
    if (quiz.dataset.vhQuizReady) return;
    quiz.dataset.vhQuizReady = 'true';

    const steps = [...quiz.querySelectorAll('[data-vh-quiz-step]')];
    const results = [...quiz.querySelectorAll('[data-vh-quiz-result]')];
    const intro = quiz.querySelector('[data-vh-quiz-intro]');
    const progress = quiz.querySelector('[data-vh-quiz-progress]');
    const progressText = quiz.querySelector('[data-vh-quiz-progress-text]');
    const history = [];
    let index = -1;

    const show = (el) => {
      [intro, ...steps, ...results].forEach((node) => node && (node.hidden = node !== el));
      const top = quiz.getBoundingClientRect().top;
      if (top < 60) window.scrollBy({ top: top - 80, behavior: 'smooth' });
      const focusTarget = el?.querySelector('h3, h4, [tabindex="-1"]');
      if (focusTarget) {
        focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });
      }
    };

    const updateProgress = () => {
      if (!progress) return;
      const done = Math.max(0, index);
      progress.style.setProperty('--vh-progress', `${(done / steps.length) * 100}%`);
      progress.hidden = index < 0 || index >= steps.length;
      if (progressText) progressText.textContent = `${Math.min(index + 1, steps.length)} / ${steps.length}`;
    };

    const finish = () => {
      const score = {};
      history.forEach((key) => (score[key] = (score[key] || 0) + 1));
      let best = 'cycle';
      for (const key of ['cycle', 'energy', 'calm']) if ((score[key] || 0) > (score[best] || 0)) best = key;
      const result = results.find((node) => node.dataset.vhQuizResult === best) || results[0];
      index = steps.length;
      updateProgress();
      show(result);
    };

    const go = (next) => {
      index = next;
      updateProgress();
      if (index >= steps.length) finish();
      else show(steps[index]);
    };

    quiz.addEventListener('click', (event) => {
      if (event.target.closest('[data-vh-quiz-start]')) {
        history.length = 0;
        go(0);
        return;
      }
      const answer = event.target.closest('[data-vh-quiz-answer]');
      if (answer) {
        history[index] = answer.dataset.vhQuizAnswer;
        history.length = index + 1;
        go(index + 1);
        return;
      }
      if (event.target.closest('[data-vh-quiz-back]') && index > 0) go(index - 1);
      if (event.target.closest('[data-vh-quiz-restart]')) {
        history.length = 0;
        go(0);
      }
    });

    if (quiz.querySelector('.vh-quiz__form-success')) {
      const successResult = quiz.querySelector('.vh-quiz__form-success').closest('[data-vh-quiz-result]');
      if (successResult) show(successResult);
    }
  });
}

/* ---------- Hochzählende Zahlen ---------- */
function initCounters(root) {
  const els = [...root.querySelectorAll('[data-vh-count]')];
  if (!els.length) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const run = (el) => {
    const target = parseFloat(el.dataset.vhCount);
    const decimals = (el.dataset.vhCount.split('.')[1] || '').length;
    if (reduce || !isFinite(target)) return;
    const start = performance.now();
    const dur = 1400;
    const step = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = (target * eased).toFixed(decimals).replace('.', ',');
      if (t < 1) requestAnimationFrame(step);
    };
    el.textContent = '0';
    requestAnimationFrame(step);
  };
  if (!('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        run(e.target);
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0.6 });
  els.forEach((el) => io.observe(el));
}

/* ---------- Init ---------- */
function init(root = document) {
  initReveal(root);
  initRails(root);
  initOffers(root);
  initStacks(root);
  initDots(root);
  initQuiz(root);
  initCounters(root);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
else init();

document.addEventListener('shopify:section:load', (event) => init(event.target));
